import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, MapPin, Star, Clock, DollarSign, Heart, Plus,
  Compass, Camera, Utensils, Mountain, Waves, Building,
  Music, ShoppingBag, Sparkles, Loader2, ExternalLink, ChevronRight
} from "lucide-react";

const CATEGORY_ICONS = {
  "adventure": Mountain,
  "culture": Building,
  "food": Utensils,
  "nature": Waves,
  "nightlife": Music,
  "shopping": ShoppingBag,
  "sightseeing": Camera,
  "tours": Compass,
};

const CATEGORY_COLORS = {
  "adventure": "bg-orange-100 text-orange-700",
  "culture": "bg-purple-100 text-purple-700",
  "food": "bg-red-100 text-red-700",
  "nature": "bg-green-100 text-green-700",
  "nightlife": "bg-indigo-100 text-indigo-700",
  "shopping": "bg-pink-100 text-pink-700",
  "sightseeing": "bg-blue-100 text-blue-700",
  "tours": "bg-teal-100 text-teal-700",
};

export default function ExploreDestination({ isOpen, onClose, onAddToBucketList, initialCountry = "" }) {
  const [searchQuery, setSearchQuery] = useState(initialCountry);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [favorites, setFavorites] = useState(new Set());

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a country or city to explore");
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await axios.post(`${API}/explore/destination`, {
        destination: searchQuery.trim(),
      });
      setResults(response.data);
      setSelectedCategory("all");
    } catch (error) {
      toast.error("Failed to explore destination. Please try again.");
      console.error("Explore error:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = (activityId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(activityId)) {
        newFavorites.delete(activityId);
      } else {
        newFavorites.add(activityId);
      }
      return newFavorites;
    });
  };

  const handleAddToBucketList = (activity) => {
    if (onAddToBucketList) {
      onAddToBucketList({
        title: activity.name,
        description: activity.description,
        location: `${activity.name}, ${results?.destination}`,
        country: results?.destination,
        category: activity.category === "food" ? "experiences" : 
                  activity.category === "adventure" ? "adventure" : "travel",
        estimated_cost: activity.price_from,
        currency: "USD",
      });
    }
    toast.success(`Added "${activity.name}" to your bucket list!`);
  };

  const filteredActivities = results?.activities?.filter(
    activity => selectedCategory === "all" || activity.category === selectedCategory
  ) || [];

  const categories = results?.activities 
    ? [...new Set(results.activities.map(a => a.category))]
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
        <div className="flex flex-col h-[85vh]">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif text-white flex items-center gap-2">
                <Compass className="w-7 h-7" />
                Explore Destinations
              </DialogTitle>
            </DialogHeader>
            <p className="text-white/80 mt-1 text-sm">
              Discover amazing things to do powered by AI
            </p>

            {/* Search Bar */}
            <div className="mt-4 flex gap-2">
              <div className="relative flex-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Enter a country or city (e.g., Jamaica, Paris, Tokyo)"
                  className="pl-10 h-12 bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="h-12 px-6 bg-white text-primary hover:bg-white/90 rounded-xl font-semibold"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-background">
            {loading && (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="relative">
                  <Sparkles className="w-16 h-16 text-primary animate-pulse" />
                </div>
                <p className="mt-4 text-foreground font-medium">Discovering amazing experiences...</p>
                <p className="text-sm text-muted-foreground">Our AI is curating the best activities for you</p>
              </div>
            )}

            {!loading && !results && (
              <div className="flex flex-col items-center justify-center h-full py-20 px-4 text-center">
                <Compass className="w-20 h-20 text-muted-foreground/40 mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">Where would you like to explore?</h3>
                <p className="text-muted-foreground max-w-md">
                  Search for any country or city and discover top-rated activities, hidden gems, 
                  local experiences, and more!
                </p>
                <div className="flex flex-wrap gap-2 mt-6 justify-center">
                  {["Jamaica", "Japan", "Italy", "Thailand", "Mexico"].map(place => (
                    <button
                      key={place}
                      onClick={() => { setSearchQuery(place); }}
                      className="px-4 py-2 bg-card border border-border rounded-full text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      {place}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {results && (
              <div className="p-4">
                {/* Destination Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-serif text-foreground">{results.destination}</h2>
                    <Badge variant="outline" className="text-xs">
                      {results.activities?.length || 0} activities
                    </Badge>
                  </div>
                  {results.description && (
                    <p className="text-muted-foreground text-sm">{results.description}</p>
                  )}
                </div>

                {/* Category Filters */}
                {categories.length > 0 && (
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        selectedCategory === "all" 
                          ? "bg-foreground text-background" 
                          : "bg-card text-foreground border border-border hover:border-foreground"
                      }`}
                    >
                      All ({results.activities?.length})
                    </button>
                    {categories.map(cat => {
                      const Icon = CATEGORY_ICONS[cat] || Compass;
                      const count = results.activities?.filter(a => a.category === cat).length;
                      return (
                        <button
                          key={cat}
                          onClick={() => setSelectedCategory(cat)}
                          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                            selectedCategory === cat 
                              ? "bg-foreground text-background" 
                              : "bg-card text-foreground border border-border hover:border-foreground"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {cat.charAt(0).toUpperCase() + cat.slice(1)} ({count})
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Activities Grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {filteredActivities.map((activity, idx) => {
                    const CategoryIcon = CATEGORY_ICONS[activity.category] || Compass;
                    const categoryColor = CATEGORY_COLORS[activity.category] || "bg-gray-100 text-gray-700";
                    const isFavorite = favorites.has(activity.id);

                    return (
                      <div
                        key={activity.id || idx}
                        className="bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-border"
                      >
                        {/* Image */}
                        <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200">
                          {activity.image_url ? (
                            <img
                              src={activity.image_url}
                              alt={activity.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <CategoryIcon className="w-12 h-12 text-gray-300" />
                            </div>
                          )}
                          
                          {/* Favorite Button */}
                          <button
                            onClick={() => toggleFavorite(activity.id)}
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-card/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
                          >
                            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
                          </button>

                          {/* Category Badge */}
                          <div className={`absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${categoryColor}`}>
                            <CategoryIcon className="w-3.5 h-3.5" />
                            {activity.category}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground mb-1 line-clamp-1">{activity.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{activity.description}</p>

                          {/* Stats Row */}
                          <div className="flex items-center gap-3 text-sm mb-3">
                            {activity.rating && (
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                <span className="font-medium text-foreground">{activity.rating}</span>
                                {activity.reviews && (
                                  <span className="text-muted-foreground">({activity.reviews})</span>
                                )}
                              </div>
                            )}
                            {activity.duration && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="w-4 h-4" />
                                <span>{activity.duration}</span>
                              </div>
                            )}
                          </div>

                          {/* Price & Action */}
                          <div className="flex items-center justify-between pt-3 border-t border-border">
                            <div>
                              {activity.price_from ? (
                                <>
                                  <span className="text-xs text-muted-foreground">From</span>
                                  <div className="flex items-center gap-1">
                                    <DollarSign className="w-4 h-4 text-green-600" />
                                    <span className="text-lg font-bold text-foreground">{activity.price_from}</span>
                                    {activity.price_unit && (
                                      <span className="text-xs text-muted-foreground">/{activity.price_unit}</span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span className="text-sm text-muted-foreground">Price varies</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleAddToBucketList(activity)}
                              className="bg-primary hover:bg-primary/90 rounded-full"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add to List
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Tips Section */}
                {results.tips && results.tips.length > 0 && (
                  <div className="mt-8 bg-card rounded-2xl p-5 border border-border">
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Travel Tips for {results.destination}
                    </h3>
                    <ul className="space-y-2">
                      {results.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
